import React from 'react';
import { Prompt, Redirect } from 'react-router-dom';
import axios from "axios";
import moment from "moment";
import download from "downloadjs";
import { parse as parseCsv } from "json2csv";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as api from "../../util/api";
import * as util from "../../util";

import Login from "../Login";

import {
  Form,
  Header,
  Instructions,
  ModalConfirmation,
  Sidebar,
  Submit,
  TablePlayer,
  TableWinner,
  Guide
} from "../../components";

// TODO: Rewrite as SCSS.
import "./index.scss";

export default class Index extends React.Component {
  constructor(props) {
    super(props);

    this.autosavedFields = [
      "capacity",
      "modified_at",
      "modified_by"
    ];

    this.defaultFields = [
      "capacity",
      "end",
      "start",
      "timezone"
    ];

    this.defaultProject = this.getDefaultProject();

    this.draftNewProject = {
      ...this.defaultProject,
      id: "new",
      title: "New Project",
      deleted: false
    };

    this.state = {
      attendeeList: [],
      draftActiveProject: JSON.parse(JSON.stringify(this.defaultProject)),
      ettEventList: [],
      isProjectCreated: false,
      isProjectNotFound: false,
      isProjectSaving: false,
      isSidebarActive: false,
      activeTab: "control",
      includeCisco: false,
      modal: {
        description: "",
        handleSubmit: undefined,
        heading: "",
        isOpen: false,
        txtAction: "",
        txtDismiss: ""
      },
      projectList: [],
      savedActiveProject: {},
      tableSort: {
        players: {
          company: undefined,
          email: undefined,
          name: undefined
        },
        winners: {
          company: undefined,
          email: undefined,
          name: undefined
        }
      }
    };
  }

  getDefaultProject() {
    return {
      ett_id: "",
      title: "",
      description: "",
      promotion_rule: "",
      capacity: 1,
      widget_visibility: true,
      show_profile_photo: true,
      winners_prompt: "",

      // TODO: Re-implement when included in back-end.
      //auto_draw: false,
      secret_url_path: "",
      raffle_url: "",
      profile_question: "",
      start: "2019-01-01T00:00:00",
      end: "2020-01-01T00:00:00",
      timezone: "-07:00#America/Los_Angeles",
      questions: [
        {
          id: "1",
          type: "none",
          description: "",
          options: [
            "",
            "",
            "",
            ""
          ],
          expected_answers: [
            {
              question_id: "1",
              data: ""
            }
          ]
        },
        {
          id: "2",
          type: "none",
          description: "",
          options: [
            "",
            "",
            "",
            ""
          ]
        }
      ],
      deleted: false
    };
  }

  // Event handler for opening modal.
  handleOpenModal = (heading, description, txtDismiss, txtAction = "", handleSubmit = undefined) => {
    this.setState({
      modal: {
        description,
        handleSubmit,
        heading,
        isOpen: true,
        txtAction,
        txtDismiss
      }
    });
  }

  // Event handler for closing modal.
  handleCloseModal = () => {
    this.setState({
      modal: {
        description: "",
        handleSubmit: undefined,
        heading: "",
        isOpen: false,
        txtAction: "",
        txtDismiss: ""
      }
    });
  }

  // Event handler for opening/closing sidebar.
  handleSidebarToggle = () => {
    this.setState({
      isSidebarActive: !this.state.isSidebarActive
    });
  }

  // Event handler for archiving a drawing project.
  handleArchiveProject = id => {
    // Fetch the latest saved version of the drawing so we don't cause undesired mutations.
    api.getDrawing(id)
      .then(({ data: drawingInfo }) => {
        // Before archiving the project, force disable visibility of widget.
        const updatedConfig = {
          ...drawingInfo,
          widget_visibility: false
        };

        // Now update; we must do in this order because we can't update a soft-deleted drawing.
        return api.updateDrawing(id, updatedConfig);
      })
      .then(() => api.archiveDrawing(id))
      .then(() => axios.all([
        api.getDrawing(id),
        api.getDrawingList()
      ]))
      .then(axios.spread(({ data: savedActiveProject }, { data: projectList }) => {
        /**
         * Update draft project from saved version as the back-end has control over the
         * deleted flag and tags and we don't want false-positives for unsaved changes.
         * 
         * Updating the project list at the same time allows us to refresh state of
         * sidebar UI in real time.
         */
        this.setState({
          draftActiveProject: savedActiveProject,
          projectList,
          savedActiveProject
        });

        this.handleCloseModal();
      }));
  }

  // Event handler for purging a drawing project.
  handlePurgeProject = id => {
    api.purgeDrawing(id)
      .then(() => api.getDrawingList())
      .then(({ data: projectList }) => {
        // Update the project list and set not found flag for redirect away from purged drawing.
        this.setState({
          isProjectNotFound: true,
          projectList
        });

        this.handleCloseModal();
      });
  }

  // Event handler for generic text/number/checkbox inputs.
  handleInputChange = (autosave = false) => ({ target: { name, value, checked, type } }) => {
    const parsePositiveInt = value => (!value && value !== 0) || value < 0 ? "" : Number.parseInt(value);

    if (name === "raffle_url"){
      if (!value.startsWith('/')){
        value = "/" + value;
      }
    }

    const setting = (
      type === "checkbox"
        ? checked
        : type === "number"
          ? parsePositiveInt(value)
          : value
    );

    // If the setting is autosaved, we will modify it now without adding it to the draft.
    autosave && this.autosaveChanges(name, setting);

    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        [name]: setting
      }
    });
  }

  // Event handler for date-time inputs.
  handleDateTimeChange = (name, [value]) => {
    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        [name]: moment(value).format("YYYY-MM-DDTHH:mm:ss")
      }
    });
  }

  // Event handler for ETT selector inputs.
  handleEttChange = ({ target: { value } }) => {
    const { ett_id, ...rest } = this.state.draftActiveProject;

    this.setState({
      draftActiveProject: {
        ...rest,
        ...value && { ett_id: value }
      }
    });
  }

  // Event handler for generic input changes of a question.
  handleQuestionInputChange = index => ({ target: { name, value } }) => {
    const { questions: draftQuestions } = { ...this.state.draftActiveProject };

    const updatedQuestion = {
      ...draftQuestions[index],
      [name]: value
    };

    const updatedQuestions = util.updateAt(draftQuestions, index, updatedQuestion);

    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        questions: updatedQuestions
      }
    });
  }

  // Event handler for changing the type of a question.
  handleQuestionTypeChange = index => ({ target: { value } }) => {
    const { questions: draftQuestions } = { ...this.state.draftActiveProject };
    const defaultQuestionsCopy = JSON.parse(JSON.stringify(this.defaultProject.questions));

    // Reset the specific question object to defaults and change the type.
    const updatedQuestion = {
      ...defaultQuestionsCopy[index],
      type: value
    };

    const updatedQuestions = util.updateAt(draftQuestions, index, updatedQuestion);

    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        questions: updatedQuestions
      }
    });
  }

  // Event handler for changing the options of a multiple choice question.
  handleQuestionOptionsChange = (questionIndex, optionIndex) => ({ target: { value } }) => {
    const { questions: draftQuestions } = { ...this.state.draftActiveProject };
    const updatedOptions = util.updateAt(draftQuestions[questionIndex].options, optionIndex, value);

    // Update this question with the updated options.
    const updatedQuestion = {
      ...draftQuestions[questionIndex],
      options: updatedOptions
    };

    const updatedQuestions = util.updateAt(draftQuestions, questionIndex, updatedQuestion);

    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        questions: updatedQuestions
      }
    });
  }

  // Event handler for changing the expected answer of a multiple choice question.
  handleQuestionExpectedAnswersChange = ({ target: { value } }) => {
    const { questions: draftQuestions } = { ...this.state.draftActiveProject };

    const updatedExpectedAnswers = [
      {
        ...draftQuestions[0].expected_answers[0],
        data: Number.parseFloat(value) || ""
      }
    ];

    const updatedQuestion = {
      ...draftQuestions[0],
      expected_answers: updatedExpectedAnswers
    };

    const updatedQuestions = util.updateAt(draftQuestions, 0, updatedQuestion);

    this.setState({
      draftActiveProject: {
        ...this.state.draftActiveProject,
        questions: updatedQuestions
      }
    });
  }

  // Event handler for submitting the form and either creating or updating a drawing.
  handleFormSubmit = (isNewProject = false) => e => {
    e.preventDefault();

    const {
      draftActiveProject,
      savedActiveProject: {
        id: savedActiveProjectId
      }
    } = this.state;

    // User wants to save their changes; check for any default/required fields which have been removed.
    const config = (
      Object.entries(draftActiveProject)
        .reduce((obj, [key, value]) => {
          const isRequired = this.defaultFields.includes(key);

          return {
            ...obj,
            [key]: isRequired ? (value || this.defaultProject[key]) : value
          };
        }, {})
    );

    this.setState({
      isProjectSaving: true
    });

    (
      isNewProject
        ? api.createDrawing(config)
        : api.updateDrawing(savedActiveProjectId, config)
    )
      .then(({ data: { id: drawingId } }) => axios.all([
        api.getDrawing(drawingId),
        api.getDrawingList()
      ]))
      .then(axios.spread(({ data: savedActiveProject }, { data: projectList }) => {
        this.setState({
          isProjectCreated: isNewProject,
          isProjectSaving: false,
          projectList,
          savedActiveProject
        });

        // Open dismiss-only dialogue to let user know the changes have been made.
        isNewProject && this.handleOpenModal("Drawing created!", "The drawing has been successfully created.", "Ok");
        !isNewProject && this.handleOpenModal("Drawing updated!", "The drawing has been successfully updated.", "Ok");
      }));
  }

  // Event handler for exporting the attendees table as a CSV.
  handleTableExport = () => {
    const {
      attendeeList,
      savedActiveProject: {
        questions
      }
    } = this.state;

    const data = attendeeList.map(({ answers, ...attendeeInfo }) => {
      const answerSubmissions = questions.reduce((obj, { type, description, options }, index) => {
        const getGenericSubmission = () => ({
          [`Question ${index + 1}: ${description}`]: answer
        });

        const getMultipleChoiceSubmission = () => options.reduce((obj, option) => {
          const key = `Question ${index + 1}: ${description} - ${option}`;
          const value = Array.isArray(answer) && answer.includes(option);

          obj[key] = value;

          return obj;
        }, {});

        const answer = type !== "none" && answers[index] && answers[index].data;

        const answerSubmission = (
          type === "none"
            ? undefined
            : type === "multiple_choice"
              ? getMultipleChoiceSubmission()
              : getGenericSubmission()
        );

        obj = {
          ...obj,
          ...answerSubmission
        };

        return obj;
      }, {});

      return {
        ...attendeeInfo,
        ...answerSubmissions
      };
    });

    const csv = parseCsv(data);
    const uri = `data:text/csv,${encodeURIComponent(csv)}`;

    download(uri, "drawing-attendees.csv", "text/csv");
  }

  // Event handler for sorting the table.
  handleTableSort = tableName => sortBy => {
    const tableSortCopy = { ...this.state.tableSort[tableName] };
    Object.keys(tableSortCopy).forEach(key => {
      tableSortCopy[key] = (
        key === sortBy
          ? (
            !tableSortCopy[key] || tableSortCopy[key] === "desc"
              ? "asc"
              : "desc"
          )
          : undefined
      );
    });

    this.setState({
      tableSort: {
        ...this.state.tableSort,
        [tableName]: tableSortCopy
      }
    });
  }

  // Event handler for deleting an attendee/winner from a drawing.
  handleTableDelete = (type, attendeeId) => {
    const {
      savedActiveProject: {
        id: savedActiveProjectId
      }
    } = this.state;

    // Validate that the function is being called with one of the two delete options.
    if (!["attendee", "winner"].includes(type)) return;

    (
      type === "attendee"
        ? api.deleteAttendee(savedActiveProjectId, attendeeId)
        : api.deleteWinner(savedActiveProjectId, attendeeId)
    )
      .then(() => api.getLimitedAttendeeListWithAllWinners({ drawingId: savedActiveProjectId }))
      .then(({ data: attendeeList }) => {
        this.setState({ attendeeList });
        (type === "attendee") && this.handleCloseModal();
      });
  }

  // Event handler for deleting all attendees from a drawing (hard reset).
  handleDeleteAllAttendees = () => {
    const {
      savedActiveProject: {
        id: savedActiveProjectId
      }
    } = this.state;

    api.deleteAllAttendees(savedActiveProjectId)
      .then(() => api.getLimitedAttendeeListWithAllWinners({ drawingId: savedActiveProjectId }))
      .then(({ data: attendeeList }) => {
        this.setState({ attendeeList });
        this.handleCloseModal();
      });
  }

  // Event handler for drawing a new winner in a drawing.
  handleTableSubmit = (excludeCompanies = []) => e => {
    e.preventDefault();

    const {
      savedActiveProject: {
        capacity,
        id: savedActiveProjectId
      }
    } = this.state;

    this.setState({
      isProjectSaving: true
    });

    api.createWinner(savedActiveProjectId, excludeCompanies)
      .then(({ data: newWinners }) => {
        if (!newWinners.length) {
          this.handleOpenModal("Could not draw new winners!", "The drawing method selected did not result in any new winners.", "Ok");

          return api.getLimitedAttendeeListWithAllWinners({ drawingId: savedActiveProjectId });
        }

        const handleSingleWinner = () => {
          const newWinnerId = newWinners[0].profile_id;

          window.open(`/drawing/${savedActiveProjectId}/winners#${newWinnerId}`, "_blank");
        };

        const handleMultipleWinners = () => {
          this.handleOpenModal("Winners drawn!", "Find the latest winners in the table of players.", "Ok");
        };

        (capacity === 1) && handleSingleWinner();
        (capacity > 1) && handleMultipleWinners();

        return api.getLimitedAttendeeListWithAllWinners({ drawingId: savedActiveProjectId });
      })
      .then(({ data: attendeeList }) => {
        this.setState({
          attendeeList,
          isProjectSaving: false
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({
          isProjectSaving: false
        });
      });
  }

  // Autosave changes to back-end instead of waiting for user to manually save.
  autosaveChanges = (name, setting) => {
    const { savedActiveProject } = this.state;
    const { id: savedActiveProjectId } = savedActiveProject;
    const isRequired = this.defaultFields.includes(name);

    const updatedConfig = {
      ...savedActiveProject,
      [name]: isRequired ? (setting || this.defaultProject[name]) : setting
    };

    api.updateDrawing(savedActiveProjectId, updatedConfig)
      .then(() => axios.all([
        api.getDrawing(savedActiveProjectId),
        api.getDrawingList()
      ]))
      .then(axios.spread(({ data: savedActiveProject }, { data: projectList }) => {
        this.setState({
          projectList,
          savedActiveProject
        });
      }));
  }

  // Checks if the draft version is different to the saved version.
  isDraftChanged = () => {
    const filterByAutosave = projectObj => (
      Object.keys(projectObj)
        .filter(key => !this.autosavedFields.includes(key))
        .reduce((obj, key) => ({
          ...obj,
          [key]: projectObj[key]
        }), {})
    );

    const { draftActiveProject, savedActiveProject } = this.state;
    const filteredDraft = filterByAutosave(draftActiveProject);
    const filteredSaved = filterByAutosave(savedActiveProject);
    const compareDraft = JSON.stringify(filteredDraft, Object.keys(filteredDraft).sort());
    const compareSaved = JSON.stringify(filteredSaved, Object.keys(filteredSaved).sort());

    return compareDraft !== compareSaved;
  }

  updateActiveProject(id) {
    const { isWelcome, isNewProject } = this.props;

    // User wants to create a new project, reset everything to defaults.
    const updateNewProject = () => {
      var draft = {
        ...this.getDefaultProject(),
        id: isNewProject ? "new" : "",
        title: "New Project",
        deleted: false
      }
      this.setState({
        savedActiveProject: draft,
        draftActiveProject: JSON.parse(JSON.stringify(draft)),
        projectList: [...(isNewProject ? [draft] : []), ...this.state.projectList.filter(p => p.id != "new")],
        attendeeList: []
      });
    };

    // User wants to access an existing project, fetch the relevant data and attendees.
    const updateExistingProject = () => {
      axios.all([
        api.getDrawing(id),
        api.getLimitedAttendeeListWithAllWinners({ drawingId: id })
      ])
        .then(axios.spread(({ data: drawingData }, attendeeList) => {
          const addEttEvent = id => {
            api.getEttEvent(id)
              .then(({ data }) => {
                const newEvent = {
                  ...data,
                  id: data._id
                };

                this.setState({
                  ettEventList: [
                    ...this.state.ettEventList,
                    newEvent
                  ]
                });
              });
          };

          const { ettEventList } = this.state;
          const defaultProjectCopy = JSON.parse(JSON.stringify(this.defaultProject));
          const defaultFieldsCopy = [...this.defaultFields];
          const isEttEventFound = ettEventList.some(({ id }) => id === drawingData.ett_id);

          drawingData.ett_id && !isEttEventFound && addEttEvent(drawingData.ett_id);

          /**
           * Draft version of the project must be "cleaned" for the following reasons:
           *
           * 1. Drawing detail form UI uses controlled inputs.
           * 2. Some fields (e.g. timezone) need a default value.
           * 3. Dealing with backwards-compatibility and malformed data values.
           */
          const cleanDraft = () => {
            // Ensure any specified fields are set to default values if not overridden by data.
            const cleanedDefaultFields = defaultFieldsCopy.reduce((obj, key) => ({
              ...obj,
              [key]: drawingData[key] || defaultProjectCopy[key]
            }), {});

            // Ensure any optional or malformed question fields are controlled and in the correct format.
            const cleanedQuestions = {
              questions: defaultProjectCopy.questions.map((defaultQuestion, index) => {
                const cleanedQuestion = Object.keys(defaultQuestion).reduce((obj, key) => ({
                  ...obj,
                  [key]: (drawingData.questions[index] && drawingData.questions[index][key]) || defaultQuestion[key]
                }), {});

                return cleanedQuestion;
              })
            };

            return {
              ...drawingData,
              ...cleanedDefaultFields,
              ...cleanedQuestions
            };
          };

          const cleanedDraft = cleanDraft();

          this.setState({
            savedActiveProject: drawingData,
            draftActiveProject: cleanedDraft,
            // Remove the "new" project from the project list as we're dealing with an existing project.
            projectList: this.state.projectList.filter(({ id }) => id !== "new"),
            attendeeList: attendeeList.data
          });
        }))
        .catch(() => {
          this.setState({
            isProjectNotFound: true
          });
        });
    };

    (isWelcome || isNewProject) ? updateNewProject() : updateExistingProject();
  }

  componentDidMount() {
    if (!util.isAuthenticated()) return;

    const { id: requestedProjectId } = this.props.match.params;

    api.getEttEventList()
      .then(({ data: { items: rawEttEventList } }) => {
        const ettEventList = util.filterList(rawEttEventList, util.dateHelper.getDateWithoutTime(), 30);

        this.setState({ ettEventList });
        this.updateActiveProject(requestedProjectId);
      });

    api.getDrawingList()
      .then(({ data: projectList }) => {
        this.setState({ projectList });
      });
  }

  componentDidUpdate(prevProps) {
    const { id: requestedProjectId } = this.props.match.params;
    const { id: prevRequestedProjectId } = prevProps.match.params;
    const { isNewProject: requestedIsNewProject } = this.props;
    const { isNewProject: prevIsNewProject } = prevProps;
    const { isProjectCreated, isProjectNotFound } = this.state;

    // Reset state if it was previously created as we're dealing we would've been redirected to it.
    isProjectCreated
      && this.setState({
        isProjectCreated: false
      });

    // Reset state if it was previously not found as we're dealing with a new project look-up now.
    isProjectNotFound
      && this.setState({
        isProjectNotFound: false
      });

    // Requesting a new project to be shown, so update the active project.
    (requestedProjectId !== prevRequestedProjectId || requestedIsNewProject != prevIsNewProject)
      && this.updateActiveProject(requestedProjectId);
  }

  renderTabbar() {
    const { isNewProject } = this.props;
    var { activeTab, draftActiveProject } = this.state;

    activeTab = isNewProject ? "config" : activeTab;

    const onTabSelected = (tab) => () => {
      this.setState({
        activeTab: tab
      });
    };

    return (
      <div className="col-12 col-lg-12">
        <div className="main-header">
          <div className="row">
            <div className="h5 font-weight-normal col-12 pb-3 text-nowrap text-truncate">{draftActiveProject.title}</div>
            <div className="col-12 tabbar">
              <div className="row d-flex justify-content-center">
                {!isNewProject && <div className={`px-4 tab active-${activeTab == "control"}`} onClick={onTabSelected("control")}>Control</div>}
                {!isNewProject && <div className={`px-4 tab active-${activeTab == "player"}`} onClick={onTabSelected("player")}>Participants</div>}
                {!isNewProject && <div className={`px-4 tab active-${activeTab == "config"}`} onClick={onTabSelected("config")}>Configuration</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderConfigTab() {

    const { isNewProject } = this.props;

    const {
      draftActiveProject,
      ettEventList,
      isProjectSaving,
      savedActiveProject
    } = this.state;

    return (
      <div className="col-12 col-lg-12">
        <div className="main-panel">
          <Form
            draftActiveProject={draftActiveProject}
            ettEventList={ettEventList}
            handleInputChange={this.handleInputChange}
            handleSelectChange={this.handleSelectChange}
            handleDateTimeChange={this.handleDateTimeChange}
            handleEttChange={this.handleEttChange}
            handleQuestionTypeChange={this.handleQuestionTypeChange}
            handleQuestionInputChange={this.handleQuestionInputChange}
            handleQuestionOptionsChange={this.handleQuestionOptionsChange}
            handleQuestionExpectedAnswersChange={this.handleQuestionExpectedAnswersChange}
          />
        </div>

        {
          !savedActiveProject.deleted
          && (
            <div className="sticky-panel py-2">
              <Submit
                handleFormSubmit={this.handleFormSubmit(isNewProject)}
                isProjectSaving={isProjectSaving}
                text={isNewProject ? "Create drawing" : "Save changes"}
              />
            </div>
          )
        }

        {
          !isNewProject && !savedActiveProject.deleted
          && (
            <div className="p-4">
              <Instructions
                activeProjectId={savedActiveProject.id}
              />
            </div>
          )
        }
      </div>
    );
  }

  renderPlayerTab() {
    const {
      attendeeList,
      isProjectSaving,
      savedActiveProject,
      tableSort
    } = this.state;
    return (
      <div className="col-12 col-lg-12">
        <div className="main-panel">
          <TableWinner
            deleted={savedActiveProject.deleted}
            handleDelete={(id) => this.handleTableDelete("winner", id)}
            handleTableSort={this.handleTableSort("winners")}
            attendeeList={attendeeList}
            tableSort={tableSort["winners"]}
          />

          <TablePlayer
            deleted={savedActiveProject.deleted}
            handleExport={this.handleTableExport}
            handleOpenDeleteAllAttendeesModal={() => this.handleOpenModal(
              "Delete all attendees?",
              "Please export all participant data if you want to backup before deleting permanently. This will purge all of this drawing's participants and you won't be able to reaccess their data.",
              "Cancel",
              "Delete all",
              this.handleDeleteAllAttendees
            )}
            handleOpenDeleteAttendeeModal={attendeeId => this.handleOpenModal(
              "Delete attendee?",
              "This will remove the attendee from the drawing and you won't be able to reaccess their data.",
              "Cancel",
              "Remove attendee",
              () => this.handleTableDelete("attendee", attendeeId)
            )}
            handleTableDelete={this.handleTableDelete}
            handleTableSort={this.handleTableSort("players")}
            isProjectSaving={isProjectSaving}
            attendeeList={attendeeList}
            tableSort={tableSort["players"]}
          />
        </div>
      </div>
    );
  }

  renderDrawingInfo() {
    const {
      savedActiveProject,
    } = this.state;
    const currentProtocolAndHost = `${window.location.protocol}//${window.location.host}`;
    const drawingUrl = api.generateUrl(savedActiveProject);
    const qrUrl = api.toQrUrl(drawingUrl);
    return (
      <div className="row">
        <div className="col-12 col-md-6 order-2 order-md-1">
          <label className="heading">
            Join drawing QR code
          </label>
          <div className="form-label-oblique pb-3">
            Join Drawing Link: &nbsp;
            <a className="form-link" target="_blank" href={drawingUrl}>
              <div className="form-link-text">{drawingUrl}</div>
            </a>
          </div>
          <div className="form-drawing-url-qr-container" style={{ width: "100%", maxWidth: 300 }}>
            <a href={qrUrl} className="hyperlink" target="_blank">
              <img
                alt="QR code for this drawing"
                className="form-drawing-url-qr-img"
                src={qrUrl}
              />
              <div className="form-drawing-url-qr-text">
                Enlarge
              </div>
            </a>
          </div>
        </div>
        <div className="col-12 col-md-6 order-1 order-md-2 mb-5">
          <label className="heading">
            Winners URL
          </label>
          <label className="form-label-oblique">Access the winners page of this drawing where the animation will play when a new winner is selected.</label>
          <a
            className="form-link"
            href={`/drawing/${savedActiveProject.id}/winners`}
            target="_blank"
          >
            <div className="form-link-text">
              {`${currentProtocolAndHost}/drawing/${savedActiveProject.id}/winners`}
            </div>
            <FontAwesomeIcon
              className="form-link-icon"
              icon={faExternalLinkAlt}
            />
          </a>
          <a
            href={`${currentProtocolAndHost}/drawing/${savedActiveProject.id}/winners`}
            target="_blank"
          >
            <div className="bg-winner mt-3">
              <div className="visit">
                Visit
              </div>
            </div>
          </a>
        </div>
      </div>
    );
  }

  renderDrawingStarter() {
    const {
      attendeeList,
      draftActiveProject,
      isProjectSaving,
      includeCisco,
      savedActiveProject: { deleted }
    } = this.state;
    const playerList = attendeeList.filter((attendee => !attendee.won));
    return (
      <div className="d-md-flex mb-5">
        <div className="position-relative mr-5 mb-3 mb-md-0">
          <label className="heading" htmlFor="js-form-capacity">All Participant(s)</label>
          <div className="text-center bg-participant" player-count={playerList.length}>
            <table>
              <tbody>
                <tr>
                  <td>
                    {
                      playerList.length > 0
                        ? playerList.length >= api.DEFAULT_LIMIT
                          ? `${api.DEFAULT_LIMIT - 1}+`
                          : `${playerList.length}`
                        : "There’s no participant in this drawing."}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className={`flex-md-1 ${playerList.length > 0 ? "" : "d-none"}`}>
          <label className="heading" htmlFor="js-form-capacity">Pick winner(s)</label>
          <input
            name="capacity"
            type="number"
            min="1"
            step="1"
            value={draftActiveProject.capacity}
            disabled={playerList.length <= 0}
            placeholder="e.g: 1"
            className="form-control form-input"
            id="js-form-capacity"
            onChange={this.handleInputChange(true)}
          />
          <div className="my-3 form-row align-items-center">
            <span>Include Cisco employee(s)</span>
              &nbsp;
              <label className="switch mt-2">
              <input
                name="include"
                id="js-form-include-cisco"
                type="checkbox"
                disabled={playerList.length <= 0}
                checked={includeCisco}
                onChange={(evt) => this.setState({ includeCisco: evt.target.checked })}
              />
              <span className="slider"></span>
            </label>
          </div>
          <button
            className="btn form-btn btn-sm btn-primary"
            disabled={isProjectSaving || playerList.length <= 0}
            onClick={this.handleTableSubmit(includeCisco ? [] : ["cisco"])}
            type="submit"
          >
            Draw
            </button>
        </div>
      </div>
    );
  }

  renderControlTab() {
    return (
      <div className="col-12 col-lg-12">
        <div className="main-panel">
          {this.renderDrawingStarter()}
          <hr className="mb-5" />
          {this.renderDrawingInfo()}
        </div>
      </div>);
  }

  renderWelcome() {
    return (
      <Guide />
    );
  }

  renderProject() {

    const {
      isNewProject
    } = this.props;

    var {
      activeTab
    } = this.state;

    activeTab = isNewProject ? "config" : activeTab;

    return (
      <div className="col-12 col-md-9">
        <div className="row no-gutters">

          {this.renderTabbar()}

          {activeTab == "config" && this.renderConfigTab()}

          {activeTab == "control" && this.renderControlTab()}

          {activeTab == "guide" && this.renderGuideTab()}

          {activeTab == "player" && this.renderPlayerTab()}

        </div>
      </div>
    );

  }

  render() {
    const { isWelcome } = this.props;

    const {
      isProjectCreated,
      isProjectNotFound,
      isSidebarActive,
      modal,
      projectList,
      savedActiveProject,
      draftActiveProject
    } = this.state;

    if (!util.isAuthenticated()) return <Login />;
    if (isProjectCreated) return <Redirect to={`/${savedActiveProject.id}`} />;
    if (isProjectNotFound) return <Redirect to="/" />;

    const { description, handleSubmit, heading, isOpen, txtAction, txtDismiss } = modal;
    const clsSidebarActive = isSidebarActive ? "" : "is-collapsed";

    return (
      <div>
        {/* Show alert prompt if the user tries to navigate away (app or browser) without saving changes. */}
        <Prompt
          when={this.isDraftChanged()}
          message={"You have unsaved changes. Are you sure you want to leave?"}
        />

        <ModalConfirmation
          description={description}
          handleCloseModal={this.handleCloseModal}
          handleSubmitModal={handleSubmit}
          heading={heading}
          isOpen={isOpen}
          txtAction={txtAction}
          txtDismiss={txtDismiss}
        />

        <Header
          isWelcome={isWelcome}
          draftActiveProject={draftActiveProject}
          clsActive={clsSidebarActive}
          handleToggle={this.handleSidebarToggle}
        />

        <div className="row no-gutters">
          <div className="col-0 col-md-3">
            <Sidebar
              activeProjectId={savedActiveProject.id}
              clsActive={clsSidebarActive}
              handleOpenArchiveModal={projectId => this.handleOpenModal(
                "Archive this drawing?",
                "This will deactivate the drawing so that it becomes read-only. You will still be able to export data.",
                "Cancel",
                "Archive drawing",
                () => this.handleArchiveProject(projectId)
              )}
              handleOpenPurgeModal={projectId => this.handleOpenModal(
                "Delete this drawing?",
                "This will purge this drawing completely along with any data relating to participants.",
                "Cancel",
                "Purge drawing",
                () => this.handlePurgeProject(projectId)
              )}
              list={projectList}
            />
          </div>
          {isWelcome ? this.renderWelcome() : this.renderProject()}
        </div>
      </div>
    );
  }
}
